// deps
import { useState, useCallback, ReactElement, useEffect, useContext } from "react";

// internal deps
import { None, Option, Some } from "@swiggy-private/js-fp";
import {
    PaymentMethod,
    PaymentMethodMeta,
    PaymentGroupDataDef,
    PaymentMethodLoadImpressionEventPayload,
} from "@swiggy-private/payment-ui";
import { useEffectOnce, useMountedRef } from "@swiggy-private/core-ui";
import { OnPayExtraParams } from "@swiggy-private/payment-types";

// helpers
import {
    generatePaymentMethodId,
    isAddNewCardPayment,
    getFrictionLayoutId,
    isMethodAlreadySelected,
    isSwiggyMoneySelected,
    isInAppUpiOnboardingPayment,
    isNewInAppUpiPayment,
} from "@payment-client/helpers/PaymentHelper";
import {
    getFirstPreferredPaymentMethodId,
    shouldSelectFirstPreferredPaymentMethod,
} from "@payment-client/helpers/PreferredHelper";
import { VALID_PAYMENT_GROUP_TYPES } from "@swiggy-private/payment-core";
import { isEmpty } from "@swiggy-private/js-utils";
import { getPaymentMethodIdToAutoSelect } from "@payment-client/helpers/PayAfterOrderHelper";

// hooks
import { useNavigate, CheckoutRoute, AnalyticsContext } from "@swiggy-private/checkout-core";
import { useOnPay } from "@payment-client/hooks/UseOnPay";
import { useInAppUPIHandler } from "@payment-client/components/show-payments/components/payment-group-handler/hooks/UseInAppUpiHandler";
import { useCreditCardEMIGroup } from "@payment-client/hooks/UseCreditCardEMIGroup";

// contexts
import { PlaceOrderContext } from "@payment-client/contexts/PlaceOrderContext";
import { PaymentPayContext } from "@payment-client/contexts/PaymentPayContext";
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import { SplitPayListContext } from "@payment-client/contexts/SplitPayListContext";

// analytics
import {
    onPayMethodClickEvent,
    onViewMorePaymentsClickEvent,
    onViewMorePaymentGroupClickEvent,
    onPaymentWalletLoadEvent,
    onPaymentWalletLoadEventParams,
    onPaymentMethodLoadEvent,
} from "@payment-client/analytics/events/Payment";

export interface ChildrenProps {
    onPay: (paymentMethodData: PaymentMethod<PaymentMethodMeta>, _extraParams?: OnPayExtraParams) => void;
    onSelect: (method: PaymentMethod<PaymentMethodMeta>) => void;
    onMorePaymentsOptionSelect: (paymentGroup: PaymentGroupDataDef, frictionGroups?: PaymentGroupDataDef[]) => void;
    onPaymentGroupClick: (paymentGroup: PaymentGroupDataDef) => void;
    onPaymentWalletLoad: (params: Omit<onPaymentWalletLoadEventParams, "impressionEvent">) => void;
    onLoad: (params: Omit<PaymentMethodLoadImpressionEventPayload, "impressionEvent">) => void;
    placeOrderInProgress: boolean;
    selectedPaymentId: string;
    inAppUpiHalfCardData: {
        showHalfCard: boolean;
        closeHalfCard: VoidFunction;
        onHalfCardLoad: VoidFunction;
        onInAppUpiHalfCardCtaClick: VoidFunction;
    };
}

export interface PaymentActionProviderProps {
    children: (props: ChildrenProps) => ReactElement | null;
    frictionGroupName?: string;
}

export function PaymentActionProvider(props: PaymentActionProviderProps): ReactElement | null {
    const isMountedRef = useMountedRef();
    const [selectedPaymentId, setSelectedPayment] = useState<Option<string>>(None);
    const { placeOrderInProgress, processingOrder } = useContext(PlaceOrderContext);
    const { paymentResponse, isPaymentMethodSelectedForOnce, setIsPaymentMethodSelectedForOnce } =
        useContext(ShowPaymentContext);
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const { withSplitPay, billTotal } = useContext(PaymentPayContext);
    const { splitPayList } = useContext(SplitPayListContext);
    const { onEMIGroupClick } = useCreditCardEMIGroup();
    const { onAddBankAccountClick, startInAppUpiOnboarding, inAppUpiHalfCardData } =
        useInAppUPIHandler(paymentResponse);

    const onPay = useOnPay();
    const navigateTo = useNavigate();

    const onSelect = useCallback(
        (method: PaymentMethod<PaymentMethodMeta>): void => {
            if (!isMountedRef.current) {
                return;
            }

            // send event
            const frictionLayoutId = getFrictionLayoutId(paymentResponse);
            onPayMethodClickEvent(method, billTotal, clickEvent, frictionLayoutId);
            if (isAddNewCardPayment(method)) {
                navigateTo(CheckoutRoute.ADD_NEW_CARD, { paymentMethod: method });
                return;
            }

            if (isInAppUpiOnboardingPayment(method)) {
                startInAppUpiOnboarding();
                return;
            }

            if (isNewInAppUpiPayment(method)) {
                onAddBankAccountClick();
                return;
            }

            if (isSwiggyMoneySelected(selectedPaymentId, method)) {
                // toggling swiggy pay
                setSelectedPayment(None);
            } else {
                setSelectedPayment((currentSelectedPaymentId) => {
                    const paymentMethodId = generatePaymentMethodId(method);
                    if (isMethodAlreadySelected(currentSelectedPaymentId.unwrapOr(""), paymentMethodId)) {
                        return None;
                    }
                    return Some(paymentMethodId);
                });
                if (!isPaymentMethodSelectedForOnce) {
                    setIsPaymentMethodSelectedForOnce(true);
                }
            }
        },
        [
            isMountedRef,
            paymentResponse,
            billTotal,
            clickEvent,
            selectedPaymentId,
            navigateTo,
            startInAppUpiOnboarding,
            onAddBankAccountClick,
            isPaymentMethodSelectedForOnce,
            setIsPaymentMethodSelectedForOnce,
        ],
    );

    const onPaymentGroupClick = useCallback(
        (paymentGroup: PaymentGroupDataDef): void => {
            // Here resides the logic to handle the click event on payment group
            // When we dont want two click on a given payment group
            const paymentGroupType = paymentGroup?.groupName?.toUpperCase();

            switch (paymentGroupType) {
                case VALID_PAYMENT_GROUP_TYPES.EMI:
                    onEMIGroupClick();
                    return;
            }

            navigateTo(CheckoutRoute.MORE_PAYMENT_METHODS, { paymentGroup });
        },
        [navigateTo, onEMIGroupClick],
    );

    const onMorePaymentsOptionSelect = useCallback(
        (paymentGroup: PaymentGroupDataDef, frictionGroups?: PaymentGroupDataDef[]): void => {
            if (!isMountedRef.current) {
                return;
            }
            const frictionLayoutId = getFrictionLayoutId(paymentResponse);

            if (frictionGroups == null) {
                onViewMorePaymentGroupClickEvent(paymentGroup.groupName, frictionLayoutId, clickEvent);
                onPaymentGroupClick(paymentGroup);
                return;
            }
            onViewMorePaymentsClickEvent(frictionLayoutId, clickEvent);
            navigateTo(CheckoutRoute.MORE_PAYMENT_GROUPS, { frictionGroups });
        },
        [isMountedRef, paymentResponse, clickEvent, navigateTo, onPaymentGroupClick],
    );

    const onLoad = useCallback(
        (params: Omit<PaymentMethodLoadImpressionEventPayload, "impressionEvent">) => {
            onPaymentMethodLoadEvent({ ...params, impressionEvent });
        },
        [impressionEvent],
    );

    const onPaymentWalletLoad = useCallback(
        (params: Omit<onPaymentWalletLoadEventParams, "impressionEvent">) => {
            onPaymentWalletLoadEvent({ ...params, impressionEvent });
        },
        [impressionEvent],
    );

    useEffect(() => {
        // Clearing selected payment method on change of split pay (toggled)
        setSelectedPayment(None);
    }, [withSplitPay]);

    // setting first preferred payment or a payment method with isSelected flag true as selected to make it expanded on load
    useEffectOnce(() => {
        if (!shouldSelectFirstPreferredPaymentMethod(!!isPaymentMethodSelectedForOnce, withSplitPay, splitPayList)) {
            const { frictionGroupName } = props;
            if (isEmpty(frictionGroupName)) {
                return;
            }
            const paymentMethodIdToAutoSelect = getPaymentMethodIdToAutoSelect(paymentResponse, frictionGroupName);
            if (
                paymentMethodIdToAutoSelect.isNone() ||
                selectedPaymentId.unwrapOr("") === paymentMethodIdToAutoSelect.unwrapOr("")
            ) {
                return;
            }
            setSelectedPayment(paymentMethodIdToAutoSelect);
            if (!isPaymentMethodSelectedForOnce) {
                setIsPaymentMethodSelectedForOnce(true);
            }
            return;
        }

        const firstPpmId = getFirstPreferredPaymentMethodId(paymentResponse);
        setSelectedPayment(firstPpmId);
        setIsPaymentMethodSelectedForOnce(true);
    });

    return props.children({
        onPay,
        onSelect,
        onMorePaymentsOptionSelect,
        onPaymentGroupClick,
        onPaymentWalletLoad,
        onLoad,
        placeOrderInProgress: placeOrderInProgress || processingOrder,
        selectedPaymentId: selectedPaymentId.unwrapOr(""),
        inAppUpiHalfCardData,
    });
}
